import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { 
  LayoutDashboard, 
  FileText, 
  Layers, 
  Image as ImageIcon, 
  Bed, 
  CheckSquare, 
  Star, 
  Settings, 
  LogOut,
  ChevronRight,
  Loader2,
  Users,
  Eye,
  Plus
} from 'lucide-react';
import { motion } from 'framer-motion';

const AdminLayout = ({ children }: { children: React.ReactNode }) => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (!user || userError) {
        navigate('/admin/login');
        return;
      }

      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .single();

      if (!roleData) {
        navigate('/admin/login');
        return;
      }
      setIsAdmin(true);
    };

    checkAdmin();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  if (isAdmin === null) {
    return (
      <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-emerald animate-spin" />
      </div>
    );
  }

  const menuItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { label: 'Website Content', icon: FileText, path: '/admin/content' },
    { label: 'Homepage Sections', icon: Layers, path: '/admin/sections' },
    { label: 'Gallery', icon: ImageIcon, path: '/admin/gallery' },
    { label: 'Rooms', icon: Bed, path: '/admin/rooms' },
    { label: 'Amenities', icon: CheckSquare, path: '/admin/amenities' },
    { label: 'Guest Reviews', icon: Star, path: '/admin/reviews' },
    { label: 'Settings', icon: Settings, path: '/admin/settings' },
  ];

  return (
    <div className="min-h-screen bg-[#F5F1E8] flex">
      {/* Sidebar */}
      <aside className={`bg-emerald text-ivory transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'} flex flex-col fixed h-full z-50`}>
        <div className="p-6 flex items-center gap-3">
          <div className="w-8 h-8 bg-ivory rounded-lg flex items-center justify-center shrink-0">
            <span className="text-emerald font-serif font-bold text-xl">W</span>
          </div>
          {sidebarOpen && <span className="font-serif font-bold text-xl tracking-tight">Walnut Admin</span>}
        </div>

        <nav className="flex-grow mt-8 px-4 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all hover:bg-white/10 ${window.location.pathname === item.path ? 'bg-white/20' : ''}`}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              {sidebarOpen && <span className="font-clean font-medium text-sm">{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-4 p-3 rounded-xl transition-all hover:bg-red-500/20 text-red-200"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {sidebarOpen && <span className="font-clean font-medium text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-grow transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'} p-8`}>
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
